'use client';

"use client";

import { useState } from "react";
import { cn } from "../../utils";
import { Skeleton } from "./skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { getAvatarColor, resolveAvatarIdentifier } from "../../utils";

/**
 * Unified Avatar Component
 *
 * Displays user avatars with a consistent fallback chain:
 * 1. Profile image URL (uploaded photo)
 * 2. Initials with deterministic background color
 */

interface UnifiedAvatarProps {
  // Data fields
  profileImageUrl?: string | null;
  avatarId?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string;
  userId?: string;

  // Display options
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  isLoading?: boolean;
}

const sizeClasses = {
  xs: "w-6 h-6 text-micro",
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
  xl: "w-16 h-16 text-lg",
};

/**
 * Generate initials from name or email
 */
function getInitials(firstName?: string | null, lastName?: string | null, email?: string): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "??";
}

export function UnifiedAvatar({
  profileImageUrl,
  avatarId,
  firstName,
  lastName,
  email,
  userId,
  size = "md",
  className,
  isLoading = false,
}: UnifiedAvatarProps) {
  const [imageError, setImageError] = useState(false);

  // Loading state
  if (isLoading) {
    return <Skeleton className={cn("rounded-full", sizeClasses[size], className)} />;
  }

  const initials = getInitials(firstName, lastName, email);
  // Use consistent identifier resolution: email → name → fallback
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || null;
  const identifier = resolveAvatarIdentifier(email, fullName, userId || "unknown");
  const bgColor = getAvatarColor(identifier);

  // Tooltip content: Name + Email
  const hasTooltipContent = fullName || email;
  const tooltipContent = hasTooltipContent ? (
    <div className="text-center">
      {fullName && <div className="font-medium text-sm">{fullName}</div>}
      {email && <div className="text-xs text-ink-supporting">{email}</div>}
    </div>
  ) : null;

  // Wrapper component for consistent tooltip
  const AvatarWrapper = ({ children }: { children: React.ReactNode }) => {
    if (!hasTooltipContent) {
      return <>{children}</>;
    }
    return (
      <TooltipProvider>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            {children}
          </TooltipTrigger>
          <TooltipContent sideOffset={5}>
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Priority 1: Profile image URL
  if (profileImageUrl && !imageError) {
    return (
      <AvatarWrapper>
        <div
          className={cn(
            "rounded-full flex items-center justify-center overflow-hidden bg-muted",
            sizeClasses[size],
            className
          )}
        >
          <img
            src={profileImageUrl}
            alt={firstName ? `${firstName}'s profile` : "User profile"}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        </div>
      </AvatarWrapper>
    );
  }

  // Priority 2: Initials with deterministic color
  return (
    <AvatarWrapper>
      <div
        className={cn(
          "flex items-center justify-center rounded-full font-medium text-logo-tile",
          sizeClasses[size],
          className
        )}
        style={{ backgroundColor: bgColor }}
      >
        {initials}
      </div>
    </AvatarWrapper>
  );
}

// Export utility functions for use elsewhere
export { getInitials, getAvatarColor };
